import {
  type ChangeEvent,
  type CSSProperties,
  type FormEvent,
  type Key,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { IconType } from "react-icons";
import {
  FaAddressBook,
  FaArrowRight,
  FaGlobe,
  FaLink,
  FaMapMarkerAlt,
  FaPalette,
  FaSave,
  FaSchool,
  FaUserShield,
} from "react-icons/fa";
import Spinner from "../components/Spinner";
import ClassLinksSection from "../components/ClassLinksSection";
import http from "../api/apiClient";
import AdminSchoolPrivacySettings, {
  PRIVACY_REVIEW_DAY_LIMITS,
  invalidPrivacyReviewDayField,
  missingPrivacyReviewPopupText,
  type PrivacyReviewSettings,
} from "./AdminSchoolPrivacySettings";
import { useCan } from "../authz/useCan";

import BackButton from "../components/navigation/BackButton";
const API_PATH = "/admin/school-settings";
const OVERVIEW_PATH = "/admin/manage/school";

type SchoolSettings = {
  schoolName: string;
  legalName: string;
  brinNumber: string;
  description: string;
  phone: string;
  email: string;
  website: string;
  street: string;
  houseNumber: string;
  houseNumberAddition: string;
  postalCode: string;
  city: string;
  country: string;
  latitude: string | number;
  longitude: string | number;
  logoUrl: string;
  primaryColor: string;
  secondaryColor: string;
} & PrivacyReviewSettings;

type SettingsPayload = Omit<SchoolSettings, "latitude" | "longitude" | keyof typeof PRIVACY_REVIEW_DAY_LIMITS> & {
  latitude: number | null;
  longitude: number | null;
} & Record<keyof typeof PRIVACY_REVIEW_DAY_LIMITS, number>;

type FieldName = keyof SchoolSettings;
type FieldType = "text" | "email" | "number" | "textarea";

type SettingsField = {
  name: FieldName;
  labelKey: string;
  labelDefault: string;
  type?: FieldType;
  step?: string;
};

type SettingsSection = {
  key: "general" | "contact" | "location" | "privacy" | "branding" | "links";
  path: string;
  titleKey: string;
  titleDefault: string;
  descriptionKey: string;
  descriptionDefault: string;
  eyebrowDefault: string;
  icon: IconType;
  decorationClass: string;
  fields: SettingsField[];
};

type NoticeMessage = {
  type: "success" | "error";
  text: string;
};

type ApiError = {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
};

const emptySettings: SchoolSettings = {
  schoolName: "",
  legalName: "",
  brinNumber: "",
  description: "",
  phone: "",
  email: "",
  website: "",
  street: "",
  houseNumber: "",
  houseNumberAddition: "",
  postalCode: "",
  city: "",
  country: "",
  latitude: "",
  longitude: "",
  logoUrl: "",
  primaryColor: "",
  secondaryColor: "",
  annualPrivacyReviewEnabled: false,
  privacyReviewIntervalDays: 365,
  privacyReviewPopupEnabled: true,
  privacyReviewPopupTitle: "",
  privacyReviewPopupMessage: "",
  privacyReviewSnoozeDays: 7,
  privacyReviewEmailEnabled: true,
  privacyReviewReminderIntervalDays: 30,
  privacyReviewEmailAdditionalMessage: "",
};

const sections: SettingsSection[] = [
  {
    key: "general",
    path: `${OVERVIEW_PATH}/general`,
    titleKey: "admin.school_settings.sections.general.title",
    titleDefault: "General",
    descriptionKey: "admin.school_settings.sections.general.description",
    descriptionDefault: "Manage the school's identity and general information.",
    eyebrowDefault: "Identity",
    icon: FaSchool,
    decorationClass: "-bottom-8 -right-4 text-indigo-100 dark:text-indigo-500/10",
    fields: [
      {
        name: "schoolName",
        labelKey: "admin.school_settings.fields.schoolName",
        labelDefault: "School name",
      },
      {
        name: "legalName",
        labelKey: "admin.school_settings.fields.legalName",
        labelDefault: "Legal name",
      },
      {
        name: "brinNumber",
        labelKey: "admin.school_settings.fields.brinNumber",
        labelDefault: "BRIN number",
      },
      {
        name: "description",
        labelKey: "admin.school_settings.fields.description",
        labelDefault: "Description",
        type: "textarea",
      },
    ],
  },
  {
    key: "contact",
    path: `${OVERVIEW_PATH}/contact`,
    titleKey: "admin.school_settings.sections.contact.title",
    titleDefault: "Contact",
    descriptionKey: "admin.school_settings.sections.contact.description",
    descriptionDefault: "Configure the contact details used throughout Lumen.",
    eyebrowDefault: "Communication",
    icon: FaAddressBook,
    decorationClass: "-bottom-8 -right-5 text-sky-100 dark:text-sky-500/10",
    fields: [
      {
        name: "phone",
        labelKey: "admin.school_settings.fields.phone",
        labelDefault: "Phone number",
      },
      {
        name: "email",
        labelKey: "admin.school_settings.fields.email",
        labelDefault: "Email address",
        type: "email",
      },
      {
        name: "website",
        labelKey: "admin.school_settings.fields.website",
        labelDefault: "Website",
      },
    ],
  },
  {
    key: "location",
    path: `${OVERVIEW_PATH}/location`,
    titleKey: "admin.school_settings.sections.location.title",
    titleDefault: "Location",
    descriptionKey: "admin.school_settings.sections.location.description",
    descriptionDefault: "Manage the address and map coordinates of the school.",
    eyebrowDefault: "Address",
    icon: FaMapMarkerAlt,
    decorationClass: "-bottom-9 -right-3 text-emerald-100 dark:text-emerald-500/10",
    fields: [
      {
        name: "street",
        labelKey: "admin.school_settings.fields.street",
        labelDefault: "Street",
      },
      {
        name: "houseNumber",
        labelKey: "admin.school_settings.fields.houseNumber",
        labelDefault: "House number",
      },
      {
        name: "houseNumberAddition",
        labelKey: "admin.school_settings.fields.houseNumberAddition",
        labelDefault: "House number addition",
      },
      {
        name: "postalCode",
        labelKey: "admin.school_settings.fields.postalCode",
        labelDefault: "Postal code",
      },
      {
        name: "city",
        labelKey: "admin.school_settings.fields.city",
        labelDefault: "City",
      },
      {
        name: "country",
        labelKey: "admin.school_settings.fields.country",
        labelDefault: "Country",
      },
      {
        name: "latitude",
        labelKey: "admin.school_settings.fields.latitude",
        labelDefault: "Latitude",
        type: "number",
        step: "any",
      },
      {
        name: "longitude",
        labelKey: "admin.school_settings.fields.longitude",
        labelDefault: "Longitude",
        type: "number",
        step: "any",
      },
    ],
  },
  {
    key: "links",
    path: `${OVERVIEW_PATH}/links`,
    titleKey: "admin.school_settings.sections.links.title",
    titleDefault: "School-wide links",
    descriptionKey: "admin.school_settings.sections.links.description",
    descriptionDefault: "Manage important links that are available throughout the school.",
    eyebrowDefault: "Communication",
    icon: FaLink,
    decorationClass: "-bottom-8 -right-4 text-emerald-100 dark:text-emerald-500/10",
    fields: [],
  },
  {
    key: "privacy",
    path: `${OVERVIEW_PATH}/privacy`,
    titleKey: "admin.school_settings.sections.privacy.title",
    titleDefault: "Privacy",
    descriptionKey: "admin.school_settings.sections.privacy.description",
    descriptionDefault: "Manage automatic privacy review reminders for parents.",
    eyebrowDefault: "Privacy",
    icon: FaUserShield,
    decorationClass: "-bottom-8 -right-4 text-violet-100 dark:text-violet-500/10",
    fields: [],
  },
  {
    key: "branding",
    path: `${OVERVIEW_PATH}/branding`,
    titleKey: "admin.school_settings.sections.branding.title",
    titleDefault: "Branding",
    descriptionKey: "admin.school_settings.sections.branding.description",
    descriptionDefault: "Configure the logo and visual identity of the school.",
    eyebrowDefault: "Appearance",
    icon: FaPalette,
    decorationClass: "-bottom-8 -right-4 text-amber-100 dark:text-amber-500/10",
    fields: [
      {
        name: "logoUrl",
        labelKey: "admin.school_settings.fields.logoUrl",
        labelDefault: "Logo URL",
      },
      {
        name: "primaryColor",
        labelKey: "admin.school_settings.fields.primaryColor",
        labelDefault: "Primary color",
      },
      {
        name: "secondaryColor",
        labelKey: "admin.school_settings.fields.secondaryColor",
        labelDefault: "Secondary color",
      },
    ],
  },
];

const normalizeSettings = (data?: Partial<SchoolSettings> | null): SchoolSettings => ({
  ...emptySettings,
  ...(data ?? {}),
  latitude: data?.latitude ?? "",
  longitude: data?.longitude ?? "",
  annualPrivacyReviewEnabled: data?.annualPrivacyReviewEnabled === true,
  privacyReviewIntervalDays: data?.privacyReviewIntervalDays ?? emptySettings.privacyReviewIntervalDays,
  privacyReviewPopupEnabled: data?.privacyReviewPopupEnabled !== false,
  privacyReviewPopupTitle: data?.privacyReviewPopupTitle ?? "",
  privacyReviewPopupMessage: data?.privacyReviewPopupMessage ?? "",
  privacyReviewSnoozeDays: data?.privacyReviewSnoozeDays ?? emptySettings.privacyReviewSnoozeDays,
  privacyReviewEmailEnabled: data?.privacyReviewEmailEnabled !== false,
  privacyReviewReminderIntervalDays: data?.privacyReviewReminderIntervalDays ?? emptySettings.privacyReviewReminderIntervalDays,
  privacyReviewEmailAdditionalMessage: data?.privacyReviewEmailAdditionalMessage ?? "",
});

const toPayload = (form: SchoolSettings): SettingsPayload => ({
  ...form,
  latitude:
    form.latitude === "" || form.latitude == null ? null : Number(form.latitude),
  longitude:
    form.longitude === "" || form.longitude == null
      ? null
      : Number(form.longitude),
  privacyReviewIntervalDays: Number(form.privacyReviewIntervalDays),
  privacyReviewSnoozeDays: Number(form.privacyReviewSnoozeDays),
  privacyReviewReminderIntervalDays: Number(form.privacyReviewReminderIntervalDays),
});

const getErrorMessage = (error: unknown, fallback: string): string => {
  const apiError = error as ApiError;
  return apiError?.response?.data?.message || apiError?.message || fallback;
};

type LdsPageShellProps = {
  children: ReactNode;
  narrow?: boolean;
};

const LdsPageShell = ({ children, narrow = false }: LdsPageShellProps) => (
  <main className="min-h-full bg-slate-50/70 px-4 py-5 dark:bg-slate-950 sm:px-6 sm:py-7 lg:px-8">
    <div
      className={`mx-auto w-full rounded-[32px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_26px_70px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-slate-950/85 sm:p-6 ${
        narrow ? "max-w-5xl" : "max-w-7xl"
      }`}
    >
      {children}
    </div>
  </main>
);

type LdsHeroProps = {
  eyebrow: string;
  title: string;
  description: string;
  icon: IconType;
};

const LdsHero = ({ eyebrow, title, description, icon: Icon }: LdsHeroProps) => (
  <section className="lds-school-enter relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 p-5 shadow-sm dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:p-6">
    <Icon className="pointer-events-none absolute right-7 top-1/2 -translate-y-1/2 text-[118px] text-indigo-200/70 dark:text-indigo-400/10 sm:right-12" />

    <div className="relative flex items-center gap-5">
      <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-violet-200 to-sky-50 text-3xl text-violet-700 shadow-lg dark:border-slate-900 dark:from-violet-800 dark:to-sky-950 dark:text-violet-100 sm:h-24 sm:w-24">
        <Icon className="text-3xl" />
      </div>

      <div className="max-w-3xl">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-indigo-700 dark:text-indigo-300">
          {eyebrow}
        </p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950 dark:text-white sm:text-4xl">
          {title}
        </h1>
        <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300 sm:text-base">
          {description}
        </p>
      </div>
    </div>
  </section>
);

type LdsNoticeProps = {
  message: NoticeMessage | null;
};

const LdsNotice = ({ message }: LdsNoticeProps) => {
  if (!message) return null;

  const success = message.type === "success";

  return (
    <div
      role="status"
      className={`lds-school-enter rounded-2xl border px-4 py-3 text-sm font-semibold shadow-sm ${
        success
          ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-950/40 dark:text-emerald-200"
          : "border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-500/30 dark:bg-rose-950/40 dark:text-rose-200"
      }`}
    >
      {message.text}
    </div>
  );
};

type LdsOverviewCardProps = {
  key?: Key;
  section: SettingsSection;
  title: string;
  description: string;
  openLabel: string;
  onClick: () => void | Promise<void>;
  delay: number;
};

const LdsOverviewCard = ({
  section,
  title,
  description,
  openLabel,
  onClick,
  delay,
}: LdsOverviewCardProps) => {
  const Icon = section.icon;
  const style = { animationDelay: `${delay}ms` } as CSSProperties;

  return (
    <button
      type="button"
      onClick={onClick}
      className="lds-school-enter group relative min-h-[220px] overflow-hidden rounded-[26px] border border-slate-200/80 bg-white px-6 py-6 text-left shadow-[0_12px_35px_rgba(15,23,42,0.05)] transition duration-300 hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-[0_22px_55px_rgba(79,70,229,0.10)] focus:outline-none focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-indigo-500/30"
      style={style}
    >
      {/* LDS hover accent: deliberately retained. */}
      <span className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-gradient-to-r from-indigo-500 via-violet-500 to-sky-400 transition-transform duration-300 ease-out group-hover:scale-x-100 group-focus-visible:scale-x-100" />

      <span className="absolute right-5 top-5 inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-400 shadow-sm transition duration-300 group-hover:border-indigo-200 group-hover:text-indigo-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-500 dark:group-hover:border-indigo-500/40 dark:group-hover:text-indigo-300">
        <FaArrowRight className="text-xs transition-transform duration-300 group-hover:translate-x-0.5" />
      </span>

      <Icon
        className={`pointer-events-none absolute text-[92px] opacity-90 ${section.decorationClass}`}
      />

      <div className="relative flex h-full flex-col pr-12">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-indigo-600 dark:text-indigo-300">
          {section.eyebrowDefault}
        </p>
        <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">
          {title}
        </h2>
        <p className="mt-3 max-w-[80%] text-sm leading-6 text-slate-600 dark:text-slate-300">
          {description}
        </p>
        <span className="mt-auto pt-8 text-sm font-semibold text-indigo-700 dark:text-indigo-300">
          {openLabel}
        </span>
      </div>
    </button>
  );
};

const fieldClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-950 shadow-sm outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500 dark:hover:border-slate-600";

const AdminSchoolSettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState<SchoolSettings>(emptySettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<NoticeMessage | null>(null);
  const canReadSchoolSettings = useCan("school.settings", "read");
  const canUpdateSchoolSettings = useCan("school.settings", "update");
  const canManageSchoolLinks = useCan("school.links", "manage");
  const canAccessSettings = canReadSchoolSettings || canUpdateSchoolSettings;

  const visibleSections = useMemo(
    () => sections.filter((section) => section.key === "links" ? canManageSchoolLinks : canAccessSettings),
    [canAccessSettings, canManageSchoolLinks]
  );

  const activeSection = useMemo<SettingsSection | null>(
    () =>
      visibleSections.find((section) =>
        location.pathname.endsWith(`/${section.key}`)
      ) ?? null,
    [location.pathname, visibleSections]
  );

  const load = useCallback(async () => {
    if (!canAccessSettings || location.pathname.endsWith("/links")) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setMessage(null);

    try {
      const { data } = await http.get<Partial<SchoolSettings>>(API_PATH);
      setForm(normalizeSettings(data));
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(
          error,
          t("admin.school_settings.messages.load_error", {
            defaultValue: "School settings could not be loaded.",
          })
        ),
      });
    } finally {
      setLoading(false);
    }
  }, [canAccessSettings, location.pathname, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setForm((previous) => ({
      ...previous,
      [name as FieldName]: value,
    }));
  };

  const handlePrivacyChange = useCallback(
    <K extends keyof PrivacyReviewSettings>(name: K, value: PrivacyReviewSettings[K]) =>
      setForm((previous) => ({ ...previous, [name]: value })),
    []
  );

  const handleSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) return;

    const invalidDays = invalidPrivacyReviewDayField(form);
    if (invalidDays) {
      setMessage({
        type: "error",
        text: t("admin.school_settings.privacy.days_range", {
          defaultValue: "Enter a whole number of days between {{min}} and {{max}}.",
          min: 1,
          max: PRIVACY_REVIEW_DAY_LIMITS[invalidDays],
        }),
      });
      return;
    }

    if (missingPrivacyReviewPopupText(form)) {
      setMessage({
        type: "error",
        text: t("admin.school_settings.privacy.popup.text_required", {
          defaultValue: "Enter a title and a message for the popup.",
        }),
      });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const { data } = await http.put<Partial<SchoolSettings>>(
        API_PATH,
        toPayload(form)
      );
      setForm(normalizeSettings(data));
      setMessage({
        type: "success",
        text: t("admin.school_settings.messages.save_success", {
          defaultValue: "School settings saved.",
        }),
      });
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(
          error,
          t("admin.school_settings.messages.save_error", {
            defaultValue: "School settings could not be saved.",
          })
        ),
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <LdsPageShell>
        <div className="flex min-h-[520px] items-center justify-center">
          <Spinner />
        </div>
      </LdsPageShell>
    );
  }

  if (!activeSection) {
    return (
      <LdsPageShell>
        <style>{`
          @keyframes ldsSchoolEnter {
            from { opacity: 0; transform: translateY(12px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .lds-school-enter {
            animation: ldsSchoolEnter .42s cubic-bezier(.22,1,.36,1) both;
          }
          @media (prefers-reduced-motion: reduce) {
            .lds-school-enter { animation: none !important; }
          }
        `}</style>

        <div className="space-y-7">
          <LdsHero
            eyebrow={t("admin.school_settings.eyebrow", {
              defaultValue: "School administration",
            })}
            title={t("admin.school_settings.title", {
              defaultValue: "School",
            })}
            description={t("admin.school_settings.subtitle", {
              defaultValue:
                "Manage school details, contact information, location and branding.",
            })}
            icon={FaSchool}
          />

          <LdsNotice message={message} />

          <section className="space-y-5">
            <header>
              <h2 className="text-xl font-semibold tracking-tight text-slate-950 dark:text-white">
                {t("admin.school_settings.overview.title", {
                  defaultValue: "School settings",
                })}
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {t("admin.school_settings.overview.description", {
                  defaultValue: "Choose the area you want to manage.",
                })}
              </p>
            </header>

            <div className="grid gap-5 md:grid-cols-2">
              {visibleSections.map((section, index) => (
                <LdsOverviewCard
                  key={section.key}
                  section={section}
                  title={t(section.titleKey, {
                    defaultValue: section.titleDefault,
                  })}
                  description={t(section.descriptionKey, {
                    defaultValue: section.descriptionDefault,
                  })}
                  openLabel={t("common.open", { defaultValue: "Open" })}
                  onClick={() => navigate(section.path)}
                  delay={70 + index * 65}
                />
              ))}
            </div>
          </section>
        </div>
      </LdsPageShell>
    );
  }

  const ActiveIcon = activeSection.icon;

  return (
    <LdsPageShell narrow>
      <style>{`
        @keyframes ldsSchoolEnter {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .lds-school-enter {
          animation: ldsSchoolEnter .42s cubic-bezier(.22,1,.36,1) both;
        }
        @media (prefers-reduced-motion: reduce) {
          .lds-school-enter { animation: none !important; }
        }
      `}</style>

      <div className="space-y-5">
        <BackButton fallback={OVERVIEW_PATH} />

        <section className="lds-school-enter overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 p-5 shadow-sm dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:p-6">
          <div className="flex items-start gap-5">
            <span className="inline-flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-violet-200 to-sky-50 text-3xl text-violet-700 shadow-lg dark:border-slate-900 dark:from-violet-800 dark:to-sky-950 dark:text-violet-100 sm:h-24 sm:w-24">
              <ActiveIcon className="text-3xl" />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-indigo-600 dark:text-indigo-300">
                {t("admin.school_settings.title", {
                  defaultValue: "School",
                })}
              </p>
              <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-950 dark:text-white">
                {t(activeSection.titleKey, {
                  defaultValue: activeSection.titleDefault,
                })}
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                {t(activeSection.descriptionKey, {
                  defaultValue: activeSection.descriptionDefault,
                })}
              </p>
            </div>
          </div>
        </section>

        <LdsNotice message={message} />

        {activeSection.key === "links" ? (
          <ClassLinksSection schoolWide />
        ) : (

        <form onSubmit={handleSave} noValidate={activeSection.key === "privacy"} className="space-y-5">
          {activeSection.key === "privacy" ? (
            <AdminSchoolPrivacySettings form={form} onChange={handlePrivacyChange} />
          ) : (
          <section
            className="lds-school-enter rounded-[26px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-7"
            style={{ animationDelay: "70ms" }}
          >
            <div className="mb-6 border-b border-slate-100 pb-5 dark:border-slate-800">
              <h2 className="text-lg font-semibold text-slate-950 dark:text-white">
                {t("admin.school_settings.form.title", {
                  defaultValue: "Settings",
                })}
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {t("admin.school_settings.form.description", {
                  defaultValue:
                    "Update the information below and save your changes.",
                })}
              </p>
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              {activeSection.fields.map((field) => (
                <label
                  key={field.name}
                  className={field.type === "textarea" ? "md:col-span-2" : ""}
                >
                  <span className="mb-2 block text-xs font-bold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                    {t(field.labelKey, {
                      defaultValue: field.labelDefault,
                    })}
                  </span>

                  {field.type === "textarea" ? (
                    <textarea
                      name={field.name}
                      value={String(form[field.name] ?? "")}
                      onChange={handleChange}
                      rows={6}
                      className={fieldClass}
                    />
                  ) : (
                    <input
                      name={field.name}
                      type={field.type ?? "text"}
                      step={field.step}
                      value={String(form[field.name] ?? "")}
                      onChange={handleChange}
                      className={fieldClass}
                    />
                  )}
                </label>
              ))}
            </div>

            {activeSection.key === "branding" && (
              <div className="mt-6 grid gap-4 border-t border-slate-100 pt-6 dark:border-slate-800 sm:grid-cols-2">
                {[
                  {
                    key: "primaryColor" as const,
                    label: t("admin.school_settings.preview.primary", {
                      defaultValue: "Primary color",
                    }),
                    fallback: "#4f46e5",
                  },
                  {
                    key: "secondaryColor" as const,
                    label: t("admin.school_settings.preview.secondary", {
                      defaultValue: "Secondary color",
                    }),
                    fallback: "#06b6d4",
                  },
                ].map((item) => {
                  const color = String(form[item.key] || item.fallback);
                  return (
                    <div
                      key={item.key}
                      className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/60"
                    >
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        {item.label}
                      </p>
                      <div className="mt-3 flex items-center gap-3">
                        <span
                          className="h-10 w-10 rounded-xl border border-black/10 shadow-sm"
                          style={{ backgroundColor: color }}
                        />
                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                          {color}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {activeSection.key === "contact" && form.website && (
              <div className="mt-6 flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300">
                <FaGlobe className="shrink-0 text-indigo-500" />
                <span className="truncate">{String(form.website)}</span>
              </div>
            )}
          </section>
          )}

          <div
            className="lds-school-enter flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between"
            style={{ animationDelay: "120ms" }}
          >
            <BackButton fallback={OVERVIEW_PATH} />

            <button
              type="submit"
              disabled={saving}
              className="inline-flex min-w-[150px] items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-600/15 transition hover:-translate-y-0.5 hover:bg-indigo-700 focus:outline-none focus:ring-4 focus:ring-indigo-500/20 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 dark:bg-indigo-500 dark:hover:bg-indigo-400"
            >
              {saving ? <Spinner /> : <FaSave />}
              {saving
                ? t("common.saving", { defaultValue: "Saving..." })
                : t("common.save", { defaultValue: "Save" })}
            </button>
          </div>
        </form>
        )}
      </div>
    </LdsPageShell>
  );
};

export default AdminSchoolSettings;
